/**
 * PipelineDetailPage — Heroku-style 5-tab view of one deployed workflow.
 *
 * Mounted at /projects/:projectId/pipelines/:workflowType. Renders inside
 * ProjectLayout so sidebar + project context come from the parent.
 *
 * Tabs:
 *   Overview  — UUID, status, version, CLI commands to trigger
 *   Activity  — past executions of this workflow (DataTable)
 *   Logs      — pointer to `zibby workflow logs -t <uuid>` (no live SSE
 *               viewer in the dashboard yet — CLI is the canonical surface
 *               and we don't reimplement it badly)
 *   Settings  — repo / trigger config (placeholder for v1; reads workflow
 *               row + lets user delete)
 *   Env       — environment variable KEYS (values never returned from the
 *               server, by design — see backend/handlers/workflow-env.js)
 *
 * No new components — uses DataTable + plain divs styled in CSS.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PipelineStatus from './PipelineStatus';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trashOutline, copyOutline, documentTextOutline, createOutline, checkmarkOutline, closeOutline, sparklesOutline, cloudUploadOutline, cubeOutline, arrowBackOutline, addOutline } from 'ionicons/icons';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { IonIcon, IonButton } from '@ionic/react';
import DataTable from '../../components/shared/DataTable/DataTable';
import ExecutionPipeline from '../../components/shared/ExecutionPipeline';
import api from '../../services/api';
import { AGENT_OPTIONS, parseAgentOption, encodeAgentOption } from '../../utils/agent-options';
import Marketplace from '../../services/marketplace';
import { timeAgo } from '../../utils/timeAgo';
import { CronExpressionParser } from 'cron-parser';
import cronstrue from 'cronstrue';
import usageService from '../../services/usage';
import TriggerModal from './TriggerModal';
import './PipelineDetailPage.css';
// Inline run-detail header reuses the standalone PipelineRunDetailPage's
// chrome (status pill / meta row / copy button). Importing its CSS
// keeps that surface DRY — same look on either render path.
import './PipelineRunDetailPage.css';

const TERMINAL_STATUSES = new Set(['completed', 'success', 'succeeded', 'failed', 'cancelled', 'skipped', 'blocked', 'insufficient_context']);

/**
 * HelpIcon — tiny question-mark icon next to a section title. Hover
 * reveals the full description as a tooltip. Trades discoverability of
 * always-visible help text for a less cluttered surface — once a user
 * has read the hint once they don't need to re-read it on every visit.
 *
 * CSS-only hover. The `data-hint` attribute is rendered into the
 * ::after pseudo-element via the rule in PipelineDetailPage.css.
 */
function HelpIcon({ hint }) {
  return (
    <span
      className="pipeline-help-icon"
      data-hint={hint}
      tabIndex={0}
      role="tooltip"
      aria-label={hint}
      title={hint /* native title is a fallback for browsers that strip ::after */}
    >
      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <text x="8" y="11.5" textAnchor="middle" fontSize="9" fontWeight="600" fill="currentColor">?</text>
      </svg>
    </span>
  );
}

/**
 * ScheduleStatusPanel — live display of a workflow's cron schedule.
 *
 * Fetches GET /workflows/{uuid}/schedule on mount, refreshes when the
 * workflow uuid changes, and renders the attached schedule (if any)
 * with a ticking countdown to the next firing.
 *
 * When no schedule is attached we render nothing — the helper CLI rows
 * below ("set / show / clear") are the call to action. When a schedule
 * IS attached, this panel sits above the helpers so the user sees
 * "what's running" before "how to change it".
 */
function ScheduleStatusPanel({ workflowUuid }) {
  const [schedule, setSchedule]   = useState(null);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState(null);
  const [now,      setNow]        = useState(() => new Date());
  const [usage,    setUsage]      = useState(null);

  // Fetch the schedule. Tied to workflowUuid — re-runs if the user
  // navigates to a different pipeline.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/workflows/${workflowUuid}/schedule`)
      .then((data) => {
        if (cancelled) return;
        setSchedule(data?.schedule || null);
      })
      .catch((err) => {
        if (cancelled) return;
        // 404 = no schedule yet (valid state). Anything else is real failure.
        if (err?.status === 404 || /404/.test(String(err?.message || ''))) {
          setSchedule(null);
        } else {
          setError(err.message || 'Failed to load schedule');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // Fetch usage in parallel with the schedule. When the account is out
  // of execution quota, the backend's scheduled-trigger Lambda silently
  // skips firing (returns 429); we surface that here so the user sees
  // "won't fire" rather than "next run in 30s" that never actually runs.
  useEffect(() => {
    let cancelled = false;
    usageService.getUsage()
      .then((data) => {
        if (cancelled) return;
        const used  = Number(data?.workflowExecutionsUsed)  || 0;
        const limit = Number(data?.workflowExecutionsLimit) || 0;
        setUsage({ used, limit, exceeded: limit > 0 && used >= limit });
      })
      .catch(() => { /* usage fetch is best-effort — don't block schedule UI */ });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // 1 Hz tick — drives the countdown without re-fetching the schedule.
  useEffect(() => {
    if (!schedule) return undefined;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [schedule]);

  // Compute next firing time from cron + timezone. cron-parser handles
  // the cron arithmetic + IANA timezones. Memoize on cron+timezone so
  // we don't re-parse every second's setNow tick.
  const nextRun = useMemo(() => {
    if (!schedule?.cron) return null;
    try {
      const interval = CronExpressionParser.parse(schedule.cron, {
        currentDate: new Date(),
        tz: schedule.timezone || 'UTC',
      });
      return interval.next().toDate();
    } catch (err) {
      console.warn('[schedule] cron parse failed:', err.message);
      return null;
    }
    // We deliberately don't depend on `now` — recomputing once per
    // mount is plenty. The displayed countdown delta uses `now`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule?.cron, schedule?.timezone, schedule?.updatedAt]);

  // Loading / error → silent. An error banner at the top of every page
  // surfaces infra issues users can't act on.
  if (loading || error) return null;

  // No schedule attached: render a slim "no schedule" indicator so the
  // user knows nothing IS attached (vs panel broken / wondering "did
  // it get deleted?"). The CTA is the helpers section below.
  if (!schedule) {
    return (
      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Active schedule
          <HelpIcon hint={'Currently no cron is attached to this workflow. Set one with `zibby workflow schedule <uuid> set "0 9 * * 1-5"` below.'} />
        </h3>
        <p className="pipeline-section-hint" style={{ opacity: 0.65, fontStyle: 'italic' }}>
          No schedule attached. Use <code>zibby workflow schedule</code> below to add one.
        </p>
      </div>
    );
  }

  const deltaMs = nextRun ? Math.max(0, nextRun.getTime() - now.getTime()) : null;
  const formatDelta = (ms) => {
    if (ms === null) return '—';
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  // Human-readable cron via cronstrue. Falls back to the raw expression
  // if cronstrue can't parse (shouldn't happen — backend validates on POST).
  let cronDescription = null;
  try {
    cronDescription = cronstrue.toString(schedule.cron, {
      use24HourTimeFormat: false,
      verbose: false,
    });
  } catch { /* malformed cron — just don't show description */ }

  // Quota state — when the account is over its monthly workflow-execution
  // limit, the scheduled-trigger Lambda short-circuits and the schedule
  // won't actually fire even though EventBridge keeps ticking. Reflect
  // that in the UI: freeze the countdown and warn next to "Next run".
  const quotaExceeded = !!usage?.exceeded;
  const quotaTooltip = quotaExceeded
    ? `Out of workflow-execution quota for this billing period (${usage.used}/${usage.limit}). Schedule will keep ticking in AWS but the run will be skipped on each fire until quota resets or you upgrade.`
    : null;

  return (
    <div className="pipeline-section">
      <h3 className="pipeline-section-title">
        Active schedule
        <HelpIcon hint="The cron currently attached to this workflow. EventBridge fires on this schedule; each fire counts toward your workflow-execution quota. Edit with the CLI commands below." />
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        padding: '12px',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Cron</div>
          <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', marginTop: '4px' }}>
            {schedule.cron}
          </div>
          {cronDescription && (
            <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.7 }}>
              {cronDescription}
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Timezone</div>
          <div style={{ fontSize: '14px', marginTop: '4px' }}>{schedule.timezone || 'UTC'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            Next run
            {quotaExceeded && (
              <span
                className="pipeline-help-icon"
                data-hint={quotaTooltip}
                style={{ color: '#f1b94c' }}
                tabIndex={0}
                role="tooltip"
                aria-label={quotaTooltip}
                title={quotaTooltip}
              >
                <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                  <path d="M8 1.5 L15 14 H1 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                  <line x1="8" y1="6" x2="8" y2="10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <circle cx="8" cy="12" r="0.9" fill="currentColor" />
                </svg>
              </span>
            )}
          </div>
          <div style={{ fontSize: '14px', marginTop: '4px', textDecoration: quotaExceeded ? 'line-through' : undefined, opacity: quotaExceeded ? 0.55 : 1 }}>
            {nextRun ? nextRun.toLocaleString() : '—'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Countdown</div>
          <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', marginTop: '4px', color: quotaExceeded ? '#f1b94c' : (deltaMs !== null && deltaMs < 60_000 ? '#7fdc7f' : undefined) }}>
            {quotaExceeded ? 'Paused (no quota)' : formatDelta(deltaMs)}
          </div>
        </div>
      </div>
      {schedule.input && Object.keys(schedule.input).length > 0 && (
        <p className="pipeline-section-hint" style={{ marginTop: 8 }}>
          <strong>Fixed input:</strong> <code>{JSON.stringify(schedule.input)}</code>
        </p>
      )}
      <p className="pipeline-section-hint" style={{ marginTop: 8, fontSize: '12px', opacity: 0.7 }}>
        Set on {schedule.createdAt ? new Date(schedule.createdAt).toLocaleString() : 'unknown'}
        {schedule.updatedAt && schedule.updatedAt !== schedule.createdAt
          ? ` · last updated ${new Date(schedule.updatedAt).toLocaleString()}`
          : ''}
      </p>
    </div>
  );
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'activity', label: 'Activity' },
  // 'logs' tab hidden — live SSE viewer isn't implemented in the dashboard,
  // CLI is the canonical surface (`zibby workflow logs -t <uuid>`). Easy
  // to re-add: put back this entry and the LogsTab render call below.
  { id: 'settings', label: 'Settings' },
  { id: 'env',      label: 'Env' },
];

const PipelineDetailPage = () => {
  const { projectId, workflowType, executionId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [pipeline, setPipeline] = useState(null);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  // Marketplace "Update available" — open the confirm modal; force-deploy
  // POST is wrapped in setUpdating so the button shows a spinner.
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Trigger modal — collects { input } per workflow.graph.stateSchema and
  // POSTs to the shared trigger endpoint (same gate as the CLI). On success
  // we navigate to the run page so the Activity tab's existing polling
  // picks up live status without an extra hop.
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const handleTriggered = useCallback((jobId) => {
    setTriggerModalOpen(false);
    navigate(`/projects/${projectId}/pipelines/${workflowType}/runs/${jobId}`);
  }, [navigate, projectId, workflowType]);

  // Inline edit state for displayName (the pencil icon next to the
  // workflow title). workflowType — the URL slug — stays immutable:
  // renaming it would break per-UUID links, sub-graph references, and
  // the subdomain registration. displayName is the only human-friendly
  // override; defaults to workflowType when absent.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef(null);

  const tabFromUrl = searchParams.get('tab');
  // When the URL carries an executionId (deep-link to a specific run),
  // force the Activity tab — that's where the inline run detail panel
  // renders. Tab querystring only matters when no executionId is set.
  const activeTab = executionId
    ? 'activity'
    : TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'overview';
  // Clicking a tab when a run is open: navigate back to the plain
  // workflow URL with that tab active. Without this, clicking
  // Overview/Settings/Env did nothing — the executionId override
  // pinned the tab back to Activity.
  const setActiveTab = (id) => {
    if (executionId) {
      navigate(`/projects/${projectId}/pipelines/${workflowType}?tab=${id}`);
    } else {
      setSearchParams({ tab: id }, { replace: true });
    }
  };

  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    try {
      const data = await api.get(`/projects/${projectId}/workflows/${workflowType}`);
      setPipeline(data);
    } catch (err) {
      // 404 = workflow doesn't exist (and isn't a built-in type or
      // marketplace template). Fall through to the NotFoundPage render
      // branch by leaving pipeline at null — no toast, no error log,
      // it's a normal user-facing state.
      if (err?.status === 404 || /404/.test(String(err?.message || ''))) {
        setPipeline(null);
        return;
      }
      console.error('Failed to fetch pipeline:', err);
      setToast?.({ show: true, message: `Could not load pipeline: ${err.message}`, color: 'danger' });
    } finally {
      setPipelineLoading(false);
    }
  }, [projectId, workflowType, setToast]);

  // App-Store-style "Update" — only meaningful when the backend overlay
  // says updateAvailable=true. Calls deploy with force=true, which
  // re-copies the latest bundle into the same workflow row preserving
  // env vars, triggers, displayName, and warm config (server-side).
  const handleUpdate = useCallback(async () => {
    const slug = pipeline?.marketplace?.slug;
    if (!slug || updating) return;
    setUpdating(true);
    try {
      await Marketplace.deployWorkflow(slug, projectId, {
        workflowName: workflowType,
        force: true,
      });
      setUpdateModalOpen(false);
      setToast?.({
        show: true,
        message: `Updated to v${pipeline.marketplace.currentVersion}`,
        color: 'success',
      });
      // Re-fetch so the pill clears + Last deployed timestamp ticks.
      await fetchPipeline();
    } catch (err) {
      console.error('Update failed:', err);
      setToast?.({
        show: true,
        message: `Update failed: ${err.message || 'unknown error'}`,
        color: 'danger',
      });
    } finally {
      setUpdating(false);
    }
  }, [pipeline, projectId, workflowType, updating, fetchPipeline, setToast]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ show: true, message: 'Copied to clipboard', color: 'success' });
    });
  };

  const handleStartRename = () => {
    setNameDraft(pipeline?.displayName || workflowType || '');
    setEditingName(true);
    // Defer focus to after paint so the input is actually mounted.
    setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 0);
  };

  const handleCancelRename = () => {
    setEditingName(false);
    setNameDraft('');
  };

  const handleSaveName = async () => {
    if (!pipeline?.uuid) {
      setToast?.({ show: true, message: 'Cannot rename: workflow has no UUID yet', color: 'danger' });
      setEditingName(false);
      return;
    }
    const trimmed = nameDraft.trim();
    if (trimmed.length > 80) {
      setToast?.({ show: true, message: 'Display name must be 80 characters or fewer', color: 'danger' });
      return;
    }
    setSavingName(true);
    try {
      // Empty string clears displayName (server falls back to
      // workflowType when absent). Non-empty values get trimmed before
      // submission so leading/trailing whitespace can't be persisted
      // (server enforces this too — defense in depth).
      const body = { displayName: trimmed === '' ? null : trimmed };
      await api.patch(`/workflows/${pipeline.uuid}`, body);
      setPipeline((prev) => prev ? { ...prev, displayName: trimmed === '' ? undefined : trimmed } : prev);
      setEditingName(false);
      setToast?.({ show: true, message: 'Workflow name updated', color: 'success' });
    } catch (err) {
      setToast?.({ show: true, message: `Rename failed: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingName(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete pipeline "${workflowType}"? This cannot be undone.`)) return;
    if (!pipeline?.uuid) {
      setToast?.({ show: true, message: 'Cannot delete: workflow has no UUID yet (deploy it first)', color: 'danger' });
      return;
    }
    try {
      // Backend wires DELETE on `/workflows/{uuid}`; the handler looks
      // up the workflow via UUID GSI and verifies project membership
      // server-side, so projectId is implied.
      await api.delete(`/workflows/${pipeline.uuid}`);
      setToast?.({ show: true, message: `Pipeline "${workflowType}" deleted`, color: 'success' });
      navigate(`/projects/${projectId}/pipelines`);
    } catch (err) {
      setToast?.({ show: true, message: `Delete failed: ${err.message}`, color: 'danger' });
    }
  };

  // ── render helpers ─────────────────────────────────────────────────

  if (pipelineLoading) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading pipeline">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-tabs">
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  // Two empty states, both routed through the shared NotFoundPage:
  //   1. pipeline === null         → workflow row not in DB AND no
  //                                   marketplace slug match → genuine 404
  //   2. pipeline.notInstalled     → marketplace template exists with
  //                                   this slug but it's not deployed
  //                                   to this project (backend returns
  //                                   a schema-only preview payload so
  //                                   trigger modals work; the rest of
  //                                   the detail page would render with
  //                                   undefined UUID / version / dates
  //                                   which is the "fake page" trap)
  if (!pipeline || pipeline.notInstalled === true) {
    const isMarketplace = pipeline?.notInstalled === true;
    const slug = pipeline?.marketplaceSlug || workflowType;
    return (
      <NotFoundPage
        code="404"
        title={isMarketplace
          ? `'${workflowType}' isn't deployed here yet`
          : 'Pipeline not found'}
        subtitle={isMarketplace
          ? "This template lives in the marketplace but hasn't been deployed to this project. Install it to start triggering runs."
          : "This pipeline doesn't exist, or it may have been deleted. Check the URL or browse your project's pipelines."}
        primaryAction={isMarketplace
          ? { label: 'Install from marketplace', to: `/projects/${projectId}/marketplace?slug=${encodeURIComponent(slug)}`, icon: cubeOutline }
          : { label: 'Back to pipelines', to: `/projects/${projectId}/pipelines`, icon: arrowBackOutline }
        }
        secondaryAction={isMarketplace
          ? { label: 'Back to pipelines', to: `/projects/${projectId}/pipelines`, icon: arrowBackOutline }
          : { label: 'Browse marketplace', to: `/projects/${projectId}/marketplace`, icon: cubeOutline }
        }
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{(pipeline?.displayName || workflowType)} · Pipelines · Zibby</title>
      </Helmet>
      <div className="pipeline-detail-page">
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            {editingName ? (
              <div className="pipeline-detail-title-edit">
                <input
                  ref={nameInputRef}
                  type="text"
                  className="pipeline-detail-title-input"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelRename();
                    }
                  }}
                  maxLength={80}
                  placeholder={workflowType}
                  disabled={savingName}
                  aria-label="Workflow display name"
                />
                <button
                  type="button"
                  className="pipeline-detail-title-btn pipeline-detail-title-save"
                  onClick={handleSaveName}
                  disabled={savingName}
                  aria-label="Save"
                  title="Save (Enter)"
                >
                  <IonIcon icon={checkmarkOutline} />
                </button>
                <button
                  type="button"
                  className="pipeline-detail-title-btn pipeline-detail-title-cancel"
                  onClick={handleCancelRename}
                  disabled={savingName}
                  aria-label="Cancel"
                  title="Cancel (Esc)"
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
            ) : (
              <>
                <h1 className="pipeline-detail-title" title={workflowType !== (pipeline.displayName || workflowType) ? `Slug: ${workflowType}` : undefined}>
                  {pipeline.displayName || workflowType}
                </h1>
                <button
                  type="button"
                  className="pipeline-detail-title-edit-btn"
                  onClick={handleStartRename}
                  aria-label="Rename workflow"
                  title="Rename"
                >
                  <IonIcon icon={createOutline} />
                </button>
                {/* Status / version / last-deployed sit next to the title
                    on the left side. Right side of the header is reserved
                    for actions (Update pill, Trigger button). */}
                <span className="pipeline-detail-meta-sep">·</span>
                <PipelineStatus
                  bundleStatus={pipeline.bundleStatus}
                  warmEnabled={pipeline.warmEnabled}
                  variant="header"
                />
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-version">v{pipeline.version || 0}</span>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-time">
                  Last deployed {pipeline.updatedAt ? timeAgo(pipeline.updatedAt) : '—'}
                </span>
              </>
            )}
          </div>
          <div className="pipeline-detail-actions">
            {/* Marketplace "Update available" pill — App-Store style.
                Only renders when the backend overlay flags this workflow
                as out-of-date relative to the marketplace listing. Sits
                LEFT of Trigger so the primary action is the rightmost
                element (Fitts' law: corner is easiest to hit). */}
            {pipeline.marketplace?.updateAvailable && (
              <button
                type="button"
                className="pipeline-detail-update-pill"
                onClick={() => setUpdateModalOpen(true)}
                title={`Update available — ${pipeline.marketplace.installedVersion || '?'} → ${pipeline.marketplace.currentVersion || '?'}`}
              >
                <IonIcon icon={sparklesOutline} aria-hidden="true" />
                Update available
                {pipeline.marketplace.currentVersion && (
                  <span className="pipeline-detail-update-pill-ver">
                    v{pipeline.marketplace.currentVersion}
                  </span>
                )}
              </button>
            )}
            <IonButton
              className="btn-white header-action-btn"
              onClick={() => setTriggerModalOpen(true)}
              disabled={pipeline.bundleStatus && pipeline.bundleStatus !== 'ready'}
              title={`Trigger ${pipeline.displayName || workflowType}`}
            >
              Trigger
            </IonButton>
          </div>
        </div>

        <TriggerModal
          isOpen={triggerModalOpen}
          onClose={() => setTriggerModalOpen(false)}
          pipeline={pipeline}
          projectId={projectId}
          workflowType={workflowType}
          onTriggered={handleTriggered}
        />

        {updateModalOpen && pipeline.marketplace && (
          <div
            className="pipeline-update-overlay"
            onClick={() => !updating && setUpdateModalOpen(false)}
            role="presentation"
          >
            <div
              className="pipeline-update-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pipeline-update-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pipeline-update-modal-head">
                <h2 id="pipeline-update-title" className="pipeline-update-modal-title">
                  Update {pipeline.displayName || pipeline.workflowType}
                </h2>
                <button
                  type="button"
                  className="pipeline-update-modal-close"
                  aria-label="Close"
                  onClick={() => !updating && setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
              <div className="pipeline-update-modal-body">
                <p className="pipeline-update-version-line">
                  <span className="pipeline-update-version-from">
                    v{pipeline.marketplace.installedVersion || '?'}
                  </span>
                  <span className="pipeline-update-version-arrow">→</span>
                  <span className="pipeline-update-version-to">
                    v{pipeline.marketplace.currentVersion || '?'}
                  </span>
                </p>
                {pipeline.marketplace.releaseNotes ? (
                  <div className="pipeline-update-notes">
                    {/* Render as plain text for v1; markdown rendering
                        is a follow-up. Whitespace is preserved so
                        authors' bullet lists still read OK. */}
                    <pre className="pipeline-update-notes-pre">
                      {pipeline.marketplace.releaseNotes}
                    </pre>
                  </div>
                ) : (
                  <p className="pipeline-update-no-notes">
                    No release notes provided by the author.
                  </p>
                )}
                <p className="pipeline-update-preserved">
                  Your environment variables, schedule, and custom
                  workflow name will be preserved.
                </p>
              </div>
              <div className="pipeline-update-modal-actions">
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--ghost"
                  onClick={() => setUpdateModalOpen(false)}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pipeline-update-btn pipeline-update-btn--primary"
                  onClick={handleUpdate}
                  disabled={updating}
                >
                  <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
                  {updating ? 'Updating…' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab strip */}
        <div className="pipeline-detail-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pipeline-detail-tab ${activeTab === tab.id ? 'pipeline-detail-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="pipeline-detail-body">
          {activeTab === 'overview' && (
            <OverviewTab
              pipeline={pipeline}
              onCopy={copyToClipboard}
              setToast={setToast}
              onPipelineUpdate={setPipeline}
            />
          )}
          {activeTab === 'activity' && (
            <ActivityTab
              projectId={projectId}
              workflowType={workflowType}
              executionId={executionId}
              pipeline={pipeline}
              setToast={setToast}
              navigate={navigate}
            />
          )}
          {/* logs tab render hidden — see TABS comment above */}
          {activeTab === 'settings' && (
            <SettingsTab
              pipeline={pipeline}
              onDelete={handleDelete}
            />
          )}
          {activeTab === 'env' && (
            <EnvTab uuid={pipeline.uuid} setToast={setToast} />
          )}
        </div>
      </div>
    </>
  );
};

// ─── Overview ────────────────────────────────────────────────────────

const OverviewTab = ({ pipeline, onCopy, setToast, onPipelineUpdate }) => {
  const triggerCmd = `zibby workflow trigger ${pipeline.uuid}`;
  // Schedule trigger commands — set/get/clear, identical to the CLI surface.
  // The set cron is just an illustrative weekday-9am default; user copies and
  // edits to taste. Cron format is standard Unix 5-field; backend converts to
  // EventBridge form so users never need to know AWS's 6-field flavor.
  const scheduleSetCmd   = `zibby workflow schedule ${pipeline.uuid} set "0 9 * * 1-5"`;
  const scheduleShowCmd  = `zibby workflow schedule ${pipeline.uuid}`;
  const scheduleClearCmd = `zibby workflow schedule ${pipeline.uuid} clear`;

  // Customer-facing webhook URL is the per-workflow subdomain
  // (`<slug>-<hash>.workflows.zibby.app/trigger`), NOT the main API path.
  // The subdomain is computed in workflow-service.computeSubdomain and
  // stored on the row at deploy time. Domain suffix depends on the stage
  // the CURRENT frontend is talking to — derive it from the API base
  // host so dev / staging / prod all point at the right tier:
  //   api-prod.zibby.app           → workflows.zibby.app
  //   api-staging.zibby.app        → workflows-staging.zibby.app
  //   api-dev.zibby.app | local    → workflows-dev.zibby.app
  // If the workflow doesn't have a subdomain on its row (shouldn't
  // happen post-deploy, but legacy / partial-save rows might), the
  // section is hidden — no URL is better than a wrong URL.
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://api-prod.zibby.app';
  const apiHost = (() => { try { return new URL(apiBase).hostname; } catch { return ''; } })();
  const workflowsDomain = apiHost.startsWith('api-staging.')
    ? 'workflows-staging.zibby.app'
    : apiHost.startsWith('api-prod.')
      ? 'workflows.zibby.app'
      : 'workflows-dev.zibby.app';
  const subdomain  = pipeline.subdomain || null;
  const webhookUrl = subdomain ? `https://${subdomain}.${workflowsDomain}/trigger` : null;
  const curlExample = webhookUrl
    ? `curl -X POST '${webhookUrl}' \\\n` +
      `  -H 'Authorization: Bearer YOUR_API_TOKEN' \\\n` +
      `  -H 'Content-Type: application/json' \\\n` +
      `  -d '{}'`
    : null;

  return (
    <div className="pipeline-tab pipeline-overview">
      <div className="pipeline-stat-grid">
        {/* `Type` removed — it's just the page title duplicated. The
            workflowType slug is shown in the breadcrumb / heading. */}
        <Stat label="UUID"             value={pipeline.uuid || '—'}        copyable onCopy={onCopy} />
        <Stat label="Version"          value={`v${pipeline.version || 0}`} />
        {pipeline.marketplace?.installedVersion && (
          <Stat
            label="Template"
            value={`${pipeline.marketplace.slug}@v${pipeline.marketplace.installedVersion}`}
          />
        )}
        <AgentStat
          uuid={pipeline.uuid}
          agent={pipeline.aiAgent || ''}
          model={pipeline.aiModel || ''}
          setToast={setToast}
          onSaved={({ aiAgent, aiModel }) => onPipelineUpdate?.({
            ...pipeline,
            aiAgent: aiAgent || null,
            aiModel: aiModel || null,
          })}
        />
        <Stat label="Last update"      value={pipeline.updatedAt ? new Date(pipeline.updatedAt).toLocaleString() : '—'} />
      </div>

      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Trigger via CLI
          <HelpIcon hint="Trigger a run from your terminal. The UUID is baked into the command — copy and paste." />
        </h3>
        <CmdRow command={triggerCmd} onCopy={onCopy} />
      </div>

      {/* Live status of the currently-attached schedule. Sits between
          "Trigger via CLI" (manual one-off) and "Trigger via schedule"
          (the helper commands to set/change/clear) so the user reads:
          "Manual trigger → What's running automatically → How to change it". */}
      {pipeline.uuid && <ScheduleStatusPanel workflowUuid={pipeline.uuid} />}

      {/* TRIGGER VIA SCHEDULE — cron-based recurring runs.
          Identical CLI flavor to "Trigger via CLI" above so users get the
          same copy-paste UX. The example uses `0 9 * * 1-5` (9am Mon-Fri UTC);
          users edit to taste. Backend (workflows-schedule.js) converts the
          Unix 5-field cron to EventBridge's 6-field form internally. */}
      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Trigger via schedule
          <HelpIcon hint="Run on a cron schedule. Standard Unix 5-field: minute hour day-of-month month day-of-week. Add --tz America/Los_Angeles for a timezone, or -p key=value for fixed input params." />
        </h3>
        <CmdRow command={scheduleSetCmd}   onCopy={onCopy} />
        <CmdRow command={scheduleShowCmd}  onCopy={onCopy} />
        <CmdRow command={scheduleClearCmd} onCopy={onCopy} />
      </div>

      {/* Trigger surfaces — currently only the webhook (HTTP POST) variant.
          Hide when the workflow's manifest declares `triggers.api: false`
          (internal-only / scheduled workflows opting out) OR when the row
          has no `subdomain` (saveWorkflow registers one on every deploy;
          absence means a partial save — better to hide than show a wrong URL). */}
      {pipeline.triggers?.api !== false && webhookUrl && (
        <div className="pipeline-section">
          <h3 className="pipeline-section-title">
            Trigger via webhook
            <HelpIcon hint="POST to this endpoint with your API token to trigger a run from CI / CD or any other system. See docs.zibby.app/cloud/triggering for full reference." />
          </h3>
          <CmdRow command={webhookUrl}  onCopy={onCopy} />
          <CmdRow command={curlExample} onCopy={onCopy} />
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, copyable, onCopy }) => (
  <div className="pipeline-stat">
    <div className="pipeline-stat-label">{label}</div>
    <div className="pipeline-stat-value">
      <span className="pipeline-stat-value-text">{value}</span>
      {copyable && value && value !== '—' && (
        <button
          type="button"
          className="pipeline-stat-copy"
          onClick={() => onCopy(value)}
          title="Copy"
        >
          <IonIcon icon={copyOutline} />
        </button>
      )}
    </div>
  </div>
);

/**
 * Per-workflow AI-agent + model selector. Looks like a Stat
 * (consistent stat-grid spacing + label) but the value is a
 * native <select>. Saves via PATCH /workflows/:uuid on change;
 * reverts on server error. "Project default" clears both
 * overrides (aiAgent: null, aiModel: null) so the workflow
 * falls back to project.aiAgent + project.aiModel.
 *
 * Option list shared with the marketplace deploy modal — keeps
 * the two surfaces in lockstep.
 */
const AgentStat = ({ uuid, agent, model, setToast, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const currentValue = encodeAgentOption({ aiAgent: agent, aiModel: model });

  const handleChange = async (e) => {
    if (!uuid || saving) return;
    const next = e.target.value;
    const previousAgent = agent || null;
    const previousModel = model || null;
    const { aiAgent: nextAgent, aiModel: nextModel } = parseAgentOption(next);
    setSaving(true);
    try {
      await api.patch(`/workflows/${uuid}`, { aiAgent: nextAgent, aiModel: nextModel });
      onSaved?.({ aiAgent: nextAgent, aiModel: nextModel });
    } catch (err) {
      setToast?.({ show: true, message: `Could not save agent: ${err?.message || 'unknown error'}`, color: 'danger' });
      onSaved?.({ aiAgent: previousAgent, aiModel: previousModel }); // restore prior
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pipeline-stat">
      <div className="pipeline-stat-label">Agent</div>
      <div className="pipeline-stat-value">
        <select
          className="pipeline-stat-select"
          value={currentValue}
          onChange={handleChange}
          disabled={!uuid || saving}
          aria-label="AI agent for this workflow"
        >
          {AGENT_OPTIONS.map((opt) => (
            <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

const CmdRow = ({ command, onCopy }) => {
  // Whole row is clickable — clicking anywhere on the row copies the
  // command (not just the small icon at the end). The dedicated icon
  // button stays for affordance + keyboard reachability, but
  // stopPropagation prevents the click from double-firing onCopy.
  return (
    <div
      className="pipeline-cmd-row"
      role="button"
      tabIndex={0}
      onClick={() => onCopy(command)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCopy(command);
        }
      }}
      title="Click to copy"
      style={{ cursor: 'pointer' }}
    >
      <code className="pipeline-cmd">{command}</code>
      <button
        type="button"
        className="pipeline-cmd-copy"
        onClick={(e) => { e.stopPropagation(); onCopy(command); }}
        title="Copy"
      >
        <IonIcon icon={copyOutline} />
      </button>
    </div>
  );
};

// ─── Activity ────────────────────────────────────────────────────────

const ActivityTab = ({ projectId, workflowType, executionId, pipeline, setToast, navigate }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination state — backend returns { executions, nextKey, hasMore }.
  // We append on subsequent fetches and stop when hasMore goes false.
  const [nextKey, setNextKey] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const fetchMoreRef = useRef(null);   // latest fetchMore — for the observer effect

  // When a specific run is selected via URL (/runs/:executionId), fetch
  // its full detail and render the per-node pipeline inline below the
  // list. Avoids the orphan "separate page" + lets the sidebar /
  // breadcrumb keep the workflow context.
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunLoading, setSelectedRunLoading] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!executionId) {
      setSelectedRun(null);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    let cancelled = false;
    const fetchOne = async () => {
      try {
        const data = await api.get(`/executions/${executionId}`);
        if (cancelled) return;
        setSelectedRun(data);
        // Same terminal-status gate as the legacy run page — once done,
        // stop polling. Avoids the infinite-fetch bug.
        if (TERMINAL_STATUSES.has(data?.status) && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        if (!cancelled) setToast?.({ show: true, message: `Could not load run: ${err.message}`, color: 'danger' });
      } finally {
        if (!cancelled) setSelectedRunLoading(false);
      }
    };
    setSelectedRunLoading(true);
    fetchOne();
    pollRef.current = setInterval(fetchOne, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [executionId, setToast]);

  // Page-fetch helper — used for both initial load and infinite-scroll
  // appends. Returns the merged data so we can swap `executions` in one
  // setState (avoids the "two re-renders" flash).
  const fetchPage = useCallback(async (cursor) => {
    const qs = new URLSearchParams({ projectId, limit: '50' });
    if (cursor) qs.set('nextKey', cursor);
    const data = await api.get(`/executions?${qs.toString()}`);
    const list = Array.isArray(data?.executions) ? data.executions
              : Array.isArray(data) ? data
              : [];
    // Server filters by `type` (analysis/test/workflow), not by the
    // workflowType slug — so we filter client-side. Fine at current
    // data volumes; revisit if a single project crosses ~1k runs.
    const filtered = list.filter(
      (e) => e.workflowType === workflowType || e.workflow_type === workflowType,
    );
    return { rows: filtered, nextKey: data?.nextKey || null, hasMore: !!data?.hasMore };
  }, [projectId, workflowType]);

  // Initial load — reset everything when projectId/workflowType changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setExecutions([]);
      setNextKey(null);
      try {
        const { rows, nextKey: nk } = await fetchPage(null);
        if (!cancelled) {
          setExecutions(rows);
          setNextKey(nk);
        }
      } catch (err) {
        if (!cancelled) {
          setToast?.({ show: true, message: `Could not load runs: ${err.message}`, color: 'danger' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchPage, setToast]);

  // Infinite scroll — IntersectionObserver on a sentinel below the
  // table fetches the next page when it scrolls into view. Stops when
  // backend reports no more pages (nextKey === null after a fetch).
  const fetchMore = useCallback(async () => {
    if (!nextKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const { rows, nextKey: nk } = await fetchPage(nextKey);
      setExecutions((prev) => [...prev, ...rows]);
      setNextKey(nk);
    } catch (err) {
      setToast?.({ show: true, message: `Could not load more: ${err.message}`, color: 'danger' });
    } finally {
      setLoadingMore(false);
    }
  }, [nextKey, loadingMore, fetchPage, setToast]);
  fetchMoreRef.current = fetchMore;

  useEffect(() => {
    if (executionId) return undefined;        // inline panel open → no auto-load
    if (!nextKey) return undefined;            // no more pages
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) fetchMoreRef.current?.();
    }, { rootMargin: '120px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [nextKey, executionId]);

  const columns = [
    {
      header: 'STATUS',
      key: 'status',
      render: (e) => <span className={`pipeline-run-status pipeline-run-status-${e.status || 'unknown'}`}>{e.status || '—'}</span>,
    },
    { header: 'EXECUTION ID', key: 'executionId', render: (e) => <code className="pipeline-run-id">{(e.executionId || e.id || '').slice(0, 12)}</code> },
    { header: 'STARTED',      key: 'startedAt',   render: (e) => (e.createdAt || e.startedAt) ? timeAgo(e.createdAt || e.startedAt) : '—' },
    { header: 'DURATION',     key: 'duration',    render: (e) => formatDuration(e) },
  ];

  // Same trick as EnvTab: pass an actions array so DataTable adds the
  // 48px-wide actions <th> at the end. Without it, DURATION becomes
  // last-child and gets `width: 48px; text-align: center` from the
  // generic table CSS.
  // Navigate Activity rows to the new pipeline-scoped run detail page
  // (PipelineRunDetailPage). The legacy `/runs/:id` route is kept intact
  // for the existing test-recipe flow — we just don't use it from here.
  const goToRun = (e) =>
    navigate(`/projects/${projectId}/pipelines/${workflowType}/runs/${e.executionId || e.id}`);

  const actions = [
    {
      label: 'View run',
      icon: documentTextOutline,
      onClick: goToRun,
    },
  ];

  // Steps array for ExecutionPipeline. The activity panel renders the
  // canonical workflow shape (every node, always) and overlays reported
  // status on top — same as AnalysisDetailsPage.
  //
  // Node-list source precedence:
  //   1. selectedRun.workflowNodes  — FROZEN snapshot stamped at run
  //                                   start. This is the canonical
  //                                   "what this run actually was"
  //                                   shape, immune to later edits of
  //                                   the template's graph.mjs. Always
  //                                   use this when present.
  //   2. pipeline.graph.nodes       — LIVE shape from the marketplace
  //                                   row overlay. Only reached for
  //                                   legacy runs that pre-date the
  //                                   executor's workflowNodes stamp.
  //                                   May misrepresent old runs if the
  //                                   template's graph has since
  //                                   changed — best-effort fallback.
  //
  // Reported steps fall into two buckets:
  //   - "node" steps         — name matches a workflow node → overlay status
  //   - "lifecycle" steps    — ECS-task phases (Setup, etc.) that don't
  //                            match any workflow node → prepended ahead
  //                            of workflow nodes so the user sees the
  //                            full execution timeline
  //
  // Status promotion logic at the bottom keeps statuses sane against
  // the overall execution status (terminal-success promotes pendings,
  // failed promotes runnings, etc.).
  const inlineSteps = useMemo(() => {
    if (!selectedRun) return [];
    const reported = selectedRun.steps || [];
    const reportedByName = new Map(reported.map((s) => [s.name, s]));
    const graphNodes = Array.isArray(pipeline?.graph?.nodes) ? pipeline.graph.nodes : [];
    const workflowNodes = Array.isArray(selectedRun.workflowNodes) && selectedRun.workflowNodes.length > 0
      ? selectedRun.workflowNodes
      : graphNodes.map((n) => ({ name: n.name || n.id, displayName: n.displayName || n.label }));

    // Build the node-row list. Every workflow node renders — pending
    // until a matching reported step shows up to overlay status / logs.
    const workflowNodeNames = new Set(workflowNodes.map((n) => n.name));
    const nodeRows = workflowNodes.map((node, idx) => {
      const r = reportedByName.get(node.name);
      const base = r ? { ...r } : { name: node.name };
      return {
        ...base,
        displayName: node.displayName ||
          base.displayName ||
          base.name?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ||
          `Step ${idx + 1}`,
        status: base.status || 'pending',
      };
    });

    // Canonical lifecycle phases that happen BEFORE the workflow's own
    // nodes run. Render them as placeholders from t=0 so the user sees
    // the full timeline immediately ("Setup → fetch_issues → …") rather
    // than Setup popping in on the next poll once the backend stamps
    // it onto execution.steps. Reported status overlays the placeholder
    // when it lands. Currently just Setup (ECS task provisioning +
    // container bootstrap); add 'Bundle Sync' / 'Egress Check' here if
    // future executor stages report under those names.
    //
    // CRITICAL: `name` must match the lowercase name the backend stamps
    // (analyze.js / workflow-executor.js write `name: 'setup'`). A
    // case-mismatched placeholder doesn't dedup against the reported
    // step → renders twice.
    const LIFECYCLE_PHASES = [
      { name: 'setup', displayName: 'Setup' },
    ];
    const lifecycleRows = LIFECYCLE_PHASES.map((phase) => {
      const r = reportedByName.get(phase.name);
      return {
        ...(r || {}),
        name: phase.name,
        displayName: r?.displayName || phase.displayName,
        status: r?.status || 'pending',
      };
    });

    // Reported steps that match neither a workflow node nor a known
    // lifecycle phase (future executor stages we haven't enumerated):
    // append AFTER lifecycle phases, BEFORE workflow nodes, so they
    // still surface visually instead of being silently dropped.
    const knownNames = new Set([
      ...workflowNodeNames,
      ...LIFECYCLE_PHASES.map((p) => p.name),
    ]);
    const orphanLifecycleRows = reported
      .filter((s) => !knownNames.has(s.name))
      .map((s) => ({
        ...s,
        displayName: s.displayName || s.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        status: s.status || 'pending',
      }));

    const out = [...lifecycleRows, ...orphanLifecycleRows, ...nodeRows];
    if (!out.length) return [];

    const exStatus = selectedRun.status;
    if (['completed', 'success', 'succeeded'].includes(exStatus)) {
      // When the workflow as a whole succeeded, distinguish two cases
      // for any node we never received a progress event for:
      //   1. The node ran but its progress message was dropped/raced —
      //      treat as completed (green ✓). Detection: it appears in the
      //      reported `steps` array, just with `status: 'pending'`.
      //   2. The graph SKIPPED the node via a conditional edge — e.g.
      //      sentry-triage routes `fetch_issues -> END` when 0 issues,
      //      bypassing classify + dispatch_alerts entirely. Detection:
      //      the node is in `workflowNodes` but never made it into
      //      `reported` (we built the row from the snapshot placeholder).
      //      Render as `skipped` (gray) so the operator can tell at a
      //      glance "nothing to do" from "ran clean".
      const reportedNames = new Set(reported.map((s) => s.name));
      out.forEach((s) => {
        if (s.status === 'pending' || s.status === 'queued') {
          s.status = reportedNames.has(s.name) ? 'completed' : 'skipped';
        }
      });
    }
    if (exStatus === 'failed') {
      out.forEach((s) => {
        if (s.status === 'running' || s.status === 'in_progress') s.status = 'failed';
        else if (s.status === 'queued' || s.status === 'pending')  s.status = 'cancelled';
      });
    }
    return out;
  }, [selectedRun]);

  return (
    <div className="pipeline-tab">
      {/* Activity list — hidden when an execution is selected so the
          focus is fully on the run detail below. Close button on the
          detail panel brings the list back. */}
      {!executionId && (
        <>
          <DataTable
            data={executions}
            columns={columns}
            loading={loading}
            rowKey="executionId"
            actions={actions}
            onRowClick={goToRun}
            emptyState={{
              title: 'No runs yet',
              description: `Trigger this pipeline with: zibby workflow trigger <uuid>`,
            }}
          />
          {/* Infinite-scroll sentinel — when this scrolls into view, the
              IntersectionObserver above kicks the next page fetch.
              Renders three states:
                - "Loading more…"  during a page fetch
                - blank            when more pages exist but not yet
                                   loading (sentinel is the trigger)
                - "End of runs"    when nextKey is null (no more pages)
              Always kept in the tree so users see the bottom marker. */}
          {!loading && executions.length > 0 && (
            <div
              ref={sentinelRef}
              className="pipeline-activity-sentinel"
              aria-hidden="true"
            >
              {loadingMore ? 'Loading more…' : (nextKey ? '' : '— End of runs —')}
            </div>
          )}
        </>
      )}

      {/* Inline run detail — shown when a row is selected via URL. The
          standalone PipelineRunDetailPage used to render this on a
          separate route, but the separate page lost the sidebar +
          breadcrumb context. Rendering inline keeps the workflow page
          intact and avoids one more route to maintain. */}
      {executionId && (
        <div className="pipeline-run-inline">
          {/* Run header — restored to match the standalone
              PipelineRunDetailPage's chrome (status pill + execution id
              + copy button + Started <ago>). The "Run <id>" h3 alone was
              too sparse once the ExecutionPipeline below renders
              placeholder steps for executions that haven't reported
              status yet; the meta row gives the user actual context
              (is it running? when did it start?). */}
          <div className="pipeline-run-inline-header">
            <div className="pipeline-run-meta">
              {selectedRun?.status && (
                <>
                  <span className={`pipeline-run-statuspill pipeline-run-statuspill-${selectedRun.status}`}>
                    {selectedRun.status}
                  </span>
                  <span className="pipeline-run-meta-sep">·</span>
                </>
              )}
              <code className="pipeline-run-id">{executionId.slice(0, 12)}</code>
              <button
                type="button"
                className="pipeline-run-copy"
                onClick={() => navigator.clipboard?.writeText(executionId).then(() => setToast?.({ show: true, message: 'Copied execution ID', color: 'success' }))}
                title="Copy execution ID"
              >
                <IonIcon icon={copyOutline} />
              </button>
              {(selectedRun?.createdAt || selectedRun?.startedAt) && (
                <>
                  <span className="pipeline-run-meta-sep">·</span>
                  <span className="pipeline-run-time">
                    Started {timeAgo(selectedRun.createdAt || selectedRun.startedAt)}
                  </span>
                </>
              )}
            </div>
            {selectedRun?.totalUsage && (Number(selectedRun.totalUsage.input_tokens || 0) + Number(selectedRun.totalUsage.output_tokens || 0)) > 0 && (
              <div className="pipeline-run-total-usage">
                <span className="pipeline-run-total-usage-tokens">
                  {formatTokensCompact(Number(selectedRun.totalUsage.input_tokens || 0))} in
                  {' · '}
                  {formatTokensCompact(Number(selectedRun.totalUsage.output_tokens || 0))} out
                  {Number(selectedRun.totalUsage.cache_read_tokens || 0) > 0 && (
                    <>{' · '}{formatTokensCompact(Number(selectedRun.totalUsage.cache_read_tokens))} cached</>
                  )}
                </span>
                {Number(selectedRun.totalUsage.estimated_cost_usd || 0) > 0 && (
                  <span className="pipeline-run-total-usage-cost">
                    {' · $'}{Number(selectedRun.totalUsage.estimated_cost_usd).toFixed(4)}
                  </span>
                )}
              </div>
            )}
          </div>
          {selectedRunLoading && !selectedRun ? (
            // Skeleton rows that mirror the eventual layout: one
            // wide "status banner" placeholder + 3-4 step rows
            // matching workflowNodes length when known. Same shape
            // as the real ExecutionPipeline so the form doesn't
            // jump when the data arrives — replaces the old plain
            // "Loading run…" text which looked broken next to the
            // populated header.
            <div className="pipeline-run-inline-skeleton" aria-busy="true">
              <div className="pipeline-run-inline-skeleton-bar pipeline-run-inline-skeleton-bar--wide" />
              {Array.from({
                length: Math.max(
                  3,
                  (Array.isArray(pipeline?.graph?.nodes) ? pipeline.graph.nodes.length : 0) + 1,
                ),
              }).map((_, i) => (
                <div key={i} className="pipeline-run-inline-skeleton-row">
                  <div className="pipeline-run-inline-skeleton-dot" />
                  <div className="pipeline-run-inline-skeleton-bar pipeline-run-inline-skeleton-bar--narrow" />
                </div>
              ))}
            </div>
          ) : !selectedRun ? (
            <div className="pipeline-run-inline-empty">Run not found.</div>
          ) : (
            <>
              {/* Run-level error banner. For setup-time failures (state
                  validation, missing inputs, etc.) the per-step logs are
                  often a 2-line stub — the actual reason lives on the
                  execution row. Surface it up top so the user can see
                  WHY the run failed without hunting through CloudWatch. */}
              {(selectedRun.status === 'failed' || selectedRun.status === 'cancelled') &&
               (selectedRun.error || selectedRun.failureReason) && (
                <div className="pipeline-run-error-banner">
                  <div className="pipeline-run-error-banner__label">Failure</div>
                  <pre className="pipeline-run-error-banner__msg">{selectedRun.error || selectedRun.failureReason}</pre>
                </div>
              )}
              <ExecutionPipeline steps={inlineSteps} executionStatus={selectedRun.status} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact K/M token formatter — mirrors ExecutionPipeline's helper,
 * duplicated here because the run-header total chip lives outside that
 * component. Trim trailing .0/.00 so "12.0K" → "12K" reads cleaner.
 */
function formatTokensCompact(n) {
  if (!n || n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 10000)   return (n / 1000).toFixed(2).replace(/\.?0+$/, '') + 'K';
  if (n < 100000)  return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (n < 1000000) return Math.round(n / 1000) + 'K';
  return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
}

const formatDuration = (e) => {
  const start = e.createdAt || e.startedAt;
  const end   = e.completedAt || e.endedAt;
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

// ─── Settings ────────────────────────────────────────────────────────

const SettingsTab = ({ pipeline, onDelete }) => (
  <div className="pipeline-tab">
    <div className="pipeline-section">
      <h3 className="pipeline-section-title">Repository</h3>
      <p className="pipeline-section-hint">
        Sources are uploaded with each <code>zibby workflow deploy</code>. The current bundle
        was built from version <strong>v{pipeline.version || 0}</strong>.
      </p>
    </div>

    <div className="pipeline-section pipeline-section-danger">
      <h3 className="pipeline-section-title">Danger zone</h3>
      <p className="pipeline-section-hint">
        Deleting this pipeline removes its bundle from S3 and unregisters the UUID. All run
        history stays. This cannot be undone.
      </p>
      <button type="button" className="pipeline-danger-btn" onClick={onDelete}>
        <IonIcon icon={trashOutline} />
        Delete pipeline
      </button>
    </div>
  </div>
);

// ─── Env ─────────────────────────────────────────────────────────────

const EnvTab = ({ uuid, setToast }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addKey, setAddKey] = useState('');
  const [addValue, setAddValue] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchKeys = useCallback(async () => {
    if (!uuid) { setLoading(false); return; }
    setLoading(true);
    try {
      // Server returns `{ keys: [...], entries: [{key,lastFour}] }`.
      // `entries` is preferred (carries the masked suffix); fall back to
      // `keys` for older API responses.
      const data = await api.get(`/workflows/${uuid}/env`);
      const list = Array.isArray(data?.entries) ? data.entries
                : Array.isArray(data?.keys)    ? data.keys
                : Array.isArray(data)          ? data
                : [];
      setKeys(list);
    } catch (err) {
      setToast?.({ show: true, message: `Could not load env: ${err.message}`, color: 'danger' });
    } finally {
      setLoading(false);
    }
  }, [uuid, setToast]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  // Submit handler for the Add modal. Server-side validation owns the
  // canonical rules (key shape, length, reserved-prefix list) — the
  // client just gates on "non-empty key" so the user gets a friendly
  // error instead of a 400 on whitespace-only input. Everything else
  // surfaces through the toast on failure.
  const submitAdd = useCallback(async () => {
    const k = addKey.trim();
    const v = addValue;
    if (!k) {
      setToast?.({ show: true, message: 'Key is required', color: 'danger' });
      return;
    }
    setAdding(true);
    try {
      await api.patch(`/workflows/${uuid}/env/${encodeURIComponent(k)}`, { value: v });
      setToast?.({ show: true, message: `Set ${k}`, color: 'success' });
      setAddOpen(false);
      setAddKey('');
      setAddValue('');
      fetchKeys();
    } catch (err) {
      setToast?.({ show: true, message: `Set failed: ${err.message}`, color: 'danger' });
    } finally {
      setAdding(false);
    }
  }, [uuid, addKey, addValue, fetchKeys, setToast]);

  const rows = keys.map((k) => (typeof k === 'string' ? { key: k, lastFour: '' } : k));
  const columns = [
    { header: 'KEY',   key: 'key',   render: (r) => <code className="pipeline-env-key">{r.key}</code> },
    {
      header: 'VALUE',
      key: 'value',
      render: (r) => (
        <span className="pipeline-env-mask">
          <span className="pipeline-env-mask-dots" aria-hidden="true">
            {'•'.repeat(24)}
          </span>
          {r.lastFour ? <span className="pipeline-env-mask-suffix">{r.lastFour}</span> : null}
        </span>
      ),
    },
  ];

  // Adding actions for two reasons:
  //   1. Delete is genuinely useful (env API supports DELETE /workflows/{uuid}/env/{key})
  //   2. Without an actions column, DataTable's `th:last-child` CSS rule
  //      (`width: 48px; text-align: center`) hijacks the VALUE column,
  //      squeezing the masked dots into a 48px box and wrapping them.
  const actions = [
    {
      label: 'Delete key',
      icon: trashOutline,
      danger: true,
      onClick: async (row) => {
        if (!window.confirm(`Delete env var "${row.key}"? You'll need to re-set it from the CLI.`)) return;
        try {
          await api.delete(`/workflows/${uuid}/env/${encodeURIComponent(row.key)}`);
          setToast?.({ show: true, message: `Deleted ${row.key}`, color: 'success' });
          fetchKeys();
        } catch (err) {
          setToast?.({ show: true, message: `Delete failed: ${err.message}`, color: 'danger' });
        }
      },
    },
  ];

  return (
    <div className="pipeline-tab">
      <div className="pipeline-env-header">
        <p className="pipeline-section-hint">
          Environment variables for this pipeline. Values are write-only — also settable via{' '}
          <code>zibby workflow env set {`<KEY>`} {`<value>`} --workflow {uuid}</code>.
        </p>
        {/* Header Add button: only show when there's at least one row.
            The empty-state card has its own centered Add button (the
            canonical "no data → big primary CTA" pattern from the
            Projects page), so rendering the header button too gave us
            doubled affordances overlapping the same skeleton lines
            DataTable still paints under the empty card. */}
        {!loading && rows.length > 0 && (
          <IonButton size="small" onClick={() => setAddOpen(true)}>
            <IonIcon slot="start" icon={addOutline} />
            Add
          </IonButton>
        )}
      </div>
      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        rowKey="key"
        actions={actions}
        emptyState={{
          title: 'No env vars set',
          description: 'Click Add to set the first one, or use the CLI: zibby workflow env set <KEY> <value>',
          actionLabel: 'Add',
          onAction: () => setAddOpen(true),
        }}
      />
      {addOpen && (
        <div
          className="pipeline-env-add-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pipeline-env-add-title"
          onClick={(e) => { if (e.target === e.currentTarget) setAddOpen(false); }}
        >
          <div className="pipeline-env-add-modal">
            <header className="pipeline-env-add-header">
              <h3 id="pipeline-env-add-title">Add environment variable</h3>
              <button
                type="button"
                className="pipeline-env-add-close"
                aria-label="Close"
                onClick={() => setAddOpen(false)}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </header>
            <div className="pipeline-env-add-body">
              <label className="pipeline-env-add-field">
                <span>Key</span>
                <input
                  type="text"
                  className="pipeline-env-add-input"
                  placeholder="SLACK_CHANNEL"
                  value={addKey}
                  onChange={(e) => setAddKey(e.target.value)}
                  // Letters / digits / underscore is the env-var convention.
                  // Strip invalid chars on input so the user can't accidentally
                  // type lowercase + dash and only learn on submit. The server
                  // also validates, so any drift in the rules surfaces there.
                  onBlur={(e) => setAddKey(e.target.value.trim())}
                  spellCheck={false}
                  autoFocus
                />
              </label>
              <label className="pipeline-env-add-field">
                <span>Value</span>
                <textarea
                  className="pipeline-env-add-textarea"
                  placeholder="#alerts-prod"
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                  rows={3}
                  spellCheck={false}
                />
              </label>
              <p className="pipeline-env-add-hint">
                Values are write-only — once saved, the server never returns
                them. To rotate, set the same key again.
              </p>
            </div>
            <footer className="pipeline-env-add-footer">
              <button
                type="button"
                className="pipeline-env-add-cancel"
                onClick={() => setAddOpen(false)}
                disabled={adding}
              >
                Cancel
              </button>
              <IonButton onClick={submitAdd} disabled={adding || !addKey.trim()}>
                {adding ? 'Saving…' : 'Save'}
              </IonButton>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineDetailPage;
